import {
  addMonths,
  differenceInCalendarDays,
  format,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import PropTypes from "prop-types";
import ReactSlider from "react-slider";
import { fetchResultMapFeatures } from "../../../actions/resultMap";
import { formatDate } from "../../../utils";
import { selectDatesWithResults } from "../../../selectors/resultMap";

// Trial dates are ISO strings ("2026-06-26").
const daysSince = (from, date) =>
  differenceInCalendarDays(parseISO(date), parseISO(from));

// Keeps a label inside the bar at both ends of the axis.
const labelShift = (pct) => (pct < 8 ? "0" : pct > 92 ? "-100%" : "-50%");

/* Breeder report dates bar: every flight is a dot placed on a true time scale,
 * so the season rhythm shows (two flights a day apart sit side by side, a
 * three-week gap stays a gap). The AUC range is two handles on the same line,
 * snapped to flight dates; the parent keeps working with date indexes.
 */
export const BreederTimeline = ({
  dates,
  selectedDate,
  aucDatesIndexes,
  setAucDatesIndexes,
  aucModeChecked,
}) => {
  const dispatch = useDispatch();
  const trial = useSelector(({ resultMap }) => resultMap.trial);
  const datesWithResults = useSelector(selectDatesWithResults);

  const first = dates[0];
  const span = daysSince(first, dates[dates.length - 1]);
  const days = dates.map((date) => daysSince(first, date));
  // A lone flight sits in the middle.
  const pct = (day) => (span ? (day / span) * 100 : 50);

  // Month names at each month start inside the range, plus the first month
  // at the left edge when the next tick leaves it room.
  const months = [];
  const start = parseISO(first);
  for (
    let month = addMonths(startOfMonth(start), 1);
    differenceInCalendarDays(month, start) <= span;
    month = addMonths(month, 1)
  )
    months.push({
      name: format(month, "MMM"),
      at: pct(differenceInCalendarDays(month, start)),
    });
  if (!months.length || months[0].at > 12)
    months.unshift({ name: format(start, "MMM"), at: 0 });

  const nearestIndex = (day) =>
    days.reduce(
      (best, d, i) =>
        Math.abs(d - day) < Math.abs(days[best] - day) ? i : best,
      0
    );
  const onAucChange = ([from, to]) => {
    const next = [nearestIndex(from), nearestIndex(to)];
    if (
      next[0] < next[1] &&
      (next[0] !== aucDatesIndexes[0] || next[1] !== aucDatesIndexes[1])
    )
      setAucDatesIndexes(next);
  };

  const showAuc = aucModeChecked && dates.length > 1;
  const [aucFrom, aucTo] = aucDatesIndexes;
  const dayLabel = (index) => format(parseISO(dates[index]), "MMM d");
  // One label for the AUC range, centred under it: two labels collided as
  // soon as the handles sat on neighbouring flights.
  const selectedIndex = dates.indexOf(selectedDate);
  const label = showAuc
    ? {
        text: `${dayLabel(aucFrom)} \u2192 ${dayLabel(aucTo)}`,
        at: (pct(days[aucFrom]) + pct(days[aucTo])) / 2,
      }
    : selectedIndex >= 0 && {
        text: dayLabel(selectedIndex),
        at: pct(days[selectedIndex]),
      };

  return (
    <div className="breeder-timeline">
      <div className="breeder-timeline-rail">
        <div className="breeder-timeline-line" />
        <div className="breeder-timeline-dots">
          {dates.map((date, index) => {
            const empty = datesWithResults && !datesWithResults.has(date);
            const tooltip = empty
              ? `${formatDate(date)}: no results available`
              : formatDate(date);

            // aria-disabled, not disabled: a disabled button gets no mouse
            // events, so its tooltip would never show.
            return (
              <button
                key={date}
                type="button"
                aria-label={tooltip}
                aria-disabled={empty}
                data-tooltip-id="tooltip"
                data-tooltip-content={tooltip}
                className={`breeder-timeline-dot${
                  date === selectedDate ? " active" : ""
                }${
                  showAuc && index >= aucFrom && index <= aucTo ? " in-auc" : ""
                }${empty ? " no-results" : ""}`}
                style={{ left: `${pct(days[index])}%` }}
                onClick={() =>
                  !empty && dispatch(fetchResultMapFeatures(trial, date, false))
                }
              />
            );
          })}
        </div>
        {showAuc && (
          // react-slider forces an inline `position: relative` on its root:
          // the wrapper does the overlay positioning instead.
          <div className="breeder-timeline-auc">
            <ReactSlider
              className="breeder-timeline-auc-slider"
              thumbClassName="breeder-timeline-thumb"
              trackClassName="breeder-timeline-track"
              min={0}
              max={span}
              value={[days[aucFrom], days[aucTo]]}
              onChange={onAucChange}
              ariaLabel={["AUC start date", "AUC end date"]}
            />
          </div>
        )}
      </div>
      <div className="breeder-timeline-months">
        {months.map(({ name, at }) => (
          <span key={`${name}-${at}`} style={{ left: `${at}%` }}>
            {name}
          </span>
        ))}
      </div>
      <div className="breeder-timeline-labels">
        {label && (
          <span
            className={showAuc ? "auc" : ""}
            style={{
              left: `${label.at}%`,
              transform: `translateX(${labelShift(label.at)})`,
            }}
          >
            {label.text}
          </span>
        )}
      </div>
    </div>
  );
};

BreederTimeline.propTypes = {
  dates: PropTypes.array.isRequired,
  aucModeChecked: PropTypes.bool,
  aucDatesIndexes: PropTypes.array,
  setAucDatesIndexes: PropTypes.func,
  selectedDate: PropTypes.string,
};
